import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SchedulerPanelTab } from '@interfaces/IScheduler';
import { PANEL_TAB_PARAM } from '@constants/schedulerParams';
import { useSchedulerActions, useSchedulerPanelOpen, useSchedulerPanelTab } from './useSchedulerStore';

const PANEL_TABS: readonly SchedulerPanelTab[] = ['overview', 'comments', 'edit', 'stock'];

/** The `tab` param as a drawer tab; anything else opens the default tab. */
function panelTabOf(value: string | null): SchedulerPanelTab | undefined {
  return PANEL_TABS.find((tab) => tab === value);
}

/** One comparable string for a (task, tab) pair — the unit both sides agree on. */
function syncKey(taskId: string | null | undefined, tab: string | null | undefined): string {
  return `${taskId ?? ''}|${tab ?? ''}`;
}

/** A drawer open on a task, as the URL records it. */
interface IOpenPanel {
  taskId: string;
  tab: SchedulerPanelTab;
}

/** `prev` with the panel's pair set to `open`, or removed when it is null —
 *  the one definition of what the drawer's params look like. */
function withPanelParams(prev: URLSearchParams, taskParam: string, open: IOpenPanel | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (open === null) {
    next.delete(taskParam);
    next.delete(PANEL_TAB_PARAM);
  } else {
    next.set(taskParam, open.taskId);
    next.set(PANEL_TAB_PARAM, open.tab);
  }
  return next;
}

interface IUsePanelUrlSyncArgs {
  /** The query param naming the open task — `openTask` on the Scheduler,
   *  `openActivity` on Activities. `tab` rides alongside it on both. */
  taskParam: string;
  /** The tasks a link may open, or undefined while they are not yet the
   *  page's own — a link must never be judged against another schedule. */
  tasks: readonly { id: string }[] | undefined;
  /** Another hook still has a write of its own pending on the URL. */
  paused: boolean;
}

/**
 * `?<taskParam>=<id>&tab=<tab>` ⇄ the shared TaskPanel drawer, in both
 * directions: opening a task writes it into the URL, and a URL that carries one
 * re-opens it. So a refresh keeps the task you were on, and the address bar is
 * a shareable link to it.
 *
 * Writes replace rather than push: clicking through twenty tasks must not bury
 * the previous page under twenty back-button steps.
 *
 * Both directions meet at `syncedRef` — the (task, tab) pair the URL and the
 * drawer last agreed on. It starts null, which means "the URL has not been read
 * yet" and holds the write back: a page may close the drawer on mount, and an
 * ungated write would mirror that empty state over the very param it is about
 * to restore from. After that, a side only acts on a pair the other side has
 * not already produced, so the two cannot chase each other.
 *
 * Both directions also wait while `paused`: every URL hook writes through
 * `setSearchParams`, whose updater reads the render's snapshot, so two writes
 * in one commit would put back whatever the other removed. The other write
 * lands first; this runs from the updated URL.
 */
export function usePanelUrlSync({ taskParam, tasks, paused }: IUsePanelUrlSyncArgs): void {
  const [params, setParams] = useSearchParams();
  const { openPanel, closePanel, setSelectedTask } = useSchedulerActions();
  const panelOpen = useSchedulerPanelOpen();
  const panelTab = useSchedulerPanelTab();
  const urlTask = params.get(taskParam);
  const urlTab = params.get(PANEL_TAB_PARAM);
  // The drawer's own task — a `create` drawer stands for no task, so it clears
  // the param like a closed one does.
  const drawerTaskId = panelOpen?.mode === 'edit' ? panelOpen.taskId : undefined;
  const syncedRef = useRef<string | null>(null);

  // URL → drawer. Also the hydration point: whatever the URL says once the
  // tasks are in hand is what the two sides start out agreeing on.
  useEffect(() => {
    if (tasks === undefined || paused) return;
    const key = syncKey(urlTask, urlTab);
    if (syncedRef.current === key) return;
    syncedRef.current = key;
    if (urlTask === null) {
      // No task in the URL — including after a schedule switch, which navigates
      // to a bare path and so drops a task the new schedule may not even hold.
      closePanel();
      return;
    }
    // A task the page does not hold opens nothing, and the stale link heals
    // into an honest URL here rather than keep claiming a drawer is open.
    // Cleared by THIS effect, not left to the one below: the tasks can arrive
    // after the first render with the drawer unchanged, and that effect —
    // which does not re-run on `tasks` — would never see the link.
    if (!tasks.some((task) => task.id === urlTask)) {
      syncedRef.current = syncKey(undefined, null);
      closePanel();
      setParams((prev) => withPanelParams(prev, taskParam, null), { replace: true });
      return;
    }
    setSelectedTask(urlTask);
    openPanel({ mode: 'edit', taskId: urlTask, initialTab: panelTabOf(urlTab) });
  }, [urlTask, urlTab, tasks, paused, taskParam, openPanel, closePanel, setSelectedTask, setParams]);

  // Drawer → URL. Deliberately blind to the drawer state in its deps above, so
  // the freshly-written pair can never be read back as an external navigation.
  // And blind to `tasks`: re-running on them would read `drawerTaskId` from the
  // same render the effect above just closed the drawer in — the pre-dispatch
  // value — and write that stale task straight back into the URL.
  useEffect(() => {
    if (syncedRef.current === null || paused) return;
    const key = syncKey(drawerTaskId, drawerTaskId === undefined ? null : panelTab);
    if (syncedRef.current === key) return;
    syncedRef.current = key;
    const open = drawerTaskId === undefined ? null : { taskId: drawerTaskId, tab: panelTab };
    setParams((prev) => withPanelParams(prev, taskParam, open), { replace: true });
  }, [drawerTaskId, panelTab, paused, taskParam, setParams]);
}
